import { useEffect, useRef, useState } from 'react'
import { useUIStore } from '@/stores/uiStore'
import { useSequencerStore, selectCurrentBuses } from '@/stores/sequencerStore'
import { getUserBus } from '@/audio/busRegistry'
import { hasWamInstance } from '@/audio/wam/WamEffectAdapter'

/**
 * Plugin-tab host for WAM (Web Audio Module) custom-element UIs.
 *
 * WAMs expose their editor as an imperative `HTMLElement` returned
 * from `instance.createGui()` — a Custom Element, typically rendered
 * inside a Shadow DOM with its own encapsulated styles. React can't
 * render it declaratively the way we do for our native plugin views
 * (e.g. `SpectrumAnalyzer`), so this component wraps the mount in a
 * `useEffect` that:
 *
 *   1. Resolves the live `WamInstance` from the store-selected bus +
 *      effect (same chain the SpectrumAnalyzer uses for analyzer-id
 *      lookup).
 *   2. Calls `instance.createGui()` — the WAM may resolve synchronously
 *      or asynchronously; we `await` either.
 *   3. Appends the returned element inside a ref'd host div.
 *   4. On unmount, calls `destroyGui(el)` if supported, then removes
 *      the element from the DOM. WAMs that don't expose `destroyGui`
 *      just get removed from the DOM — their resources are freed on
 *      the next `instance.destroy()` call anyway.
 *
 * The instance itself is owned by the `WamEffectAdapter`'s handle and
 * is disposed when the bus slot is removed, not when this component
 * unmounts. That means the same WAM instance survives tab switches,
 * so opening + closing + re-opening the Plugin tab doesn't
 * re-instantiate the DSP (which would orphan any internal state).
 */
interface WamGuiProps {
  fullScreen?: boolean
}

export function WamGui({ fullScreen = false }: WamGuiProps = {}) {
  const selected = useUIStore(s => s.selectedPlugin)
  const buses = useSequencerStore(selectCurrentBuses)
  const hostRef = useRef<HTMLDivElement | null>(null)
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'ready' | 'missing'
  >('idle')

  const effect = selected
    ? buses
        .find(b => b.id === selected.busId)
        ?.effects?.find(e => e.id === selected.effectId)
    : null

  useEffect(() => {
    // WAM instantiation is inherently async (createInstance returns a
    // promise); the status transitions here mirror an external system
    // resolving, which is exactly the React Compiler set-state-in-effect
    // rule's carve-out. Disable it locally so we can push terminal
    // sync-resolvable states without useReducer ceremony.
    /* eslint-disable react-hooks/set-state-in-effect */
    if (!selected || !effect) return
    const host = hostRef.current
    if (!host) return

    const bus = getUserBus(selected.busId)
    if (!bus) {
      setStatus('missing')
      return
    }

    const handle = bus.getPluginHandle(selected.effectId)
    if (!handle || !hasWamInstance(handle)) {
      setStatus('missing')
      return
    }

    // The WAM instance itself is constructed asynchronously inside the
    // adapter (createInstance is async). If the caller opens the Plugin
    // tab before instantiation completes, `getWamInstance()` returns
    // null; we retry every frame until it resolves or the selection
    // changes.
    let cancelled = false
    let mountedEl: HTMLElement | null = null
    let mountedInstance = handle.getWamInstance()
    let retryRaf = 0

    const mount = async () => {
      const instance = handle.getWamInstance()
      if (!instance) {
        // Not ready yet — poll on RAF.
        retryRaf = requestAnimationFrame(mount)
        return
      }

      mountedInstance = instance
      setStatus('loading')
      try {
        const el = await instance.createGui()
        if (cancelled) {
          // User changed selection before we finished loading.
          try {
            instance.destroyGui?.(el)
          } catch {
            /* ignore */
          }
          return
        }
        mountedEl = el
        host.appendChild(el)
        setStatus('ready')
      } catch (err) {
        console.warn('[wam] createGui failed:', err)
        setStatus('missing')
      }
    }

    void mount()

    return () => {
      cancelled = true
      if (retryRaf) cancelAnimationFrame(retryRaf)
      if (mountedEl && mountedInstance) {
        try {
          mountedInstance.destroyGui?.(mountedEl)
        } catch {
          /* ignore */
        }
        try {
          if (mountedEl.parentElement === host) host.removeChild(mountedEl)
        } catch {
          /* already removed */
        }
      }
    }
  }, [selected, effect])

  if (!selected || !effect) {
    return (
      <div className="flex flex-1 min-h-[20rem] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-brd-subtle bg-surface-sunken/60 px-6 py-12 text-center">
        <p className="text-sm font-medium text-content-primary">
          No WAM selected
        </p>
      </div>
    )
  }

  return (
    <div
      className={`flex flex-1 min-h-0 flex-col overflow-auto rounded-lg border border-brd-subtle bg-surface-raised/60 ${
        fullScreen ? 'p-6' : 'p-4'
      }`}
    >
      <header className="mb-3 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-content-primary">
          {effect.pluginId?.replace(/^wam-/, '') ?? 'WAM plugin'}
        </h3>
        <span className="font-mono text-[10px] uppercase tracking-wider text-content-tertiary">
          {status === 'ready' ? 'loaded' : status}
        </span>
      </header>
      <div
        ref={hostRef}
        className="wam-host flex-1 min-h-0 overflow-auto rounded-md bg-surface-sunken/40"
      />
      {status === 'missing' && (
        <p className="mt-3 text-xs text-content-secondary">
          This WAM failed to load. Check the console for the module URL.
        </p>
      )}
    </div>
  )
}
